import React from 'react';
import moment from 'moment';
import { FormattedMessage } from 'react-intl';
import startCase from 'lodash.startcase';
import camelCase from 'lodash.camelcase';

// This array holds the accepted date formats that the app uses, what the backend generates etc.
const acceptedDateInputFormats = ['YYYY-MM-DDTHH:mm:ss'];
const DEFAULT_DATE_FORMAT = 'MMM DD, YYYY';
// format date to MMM DD, YYYY
export const date = (val, dateOutputFormat = DEFAULT_DATE_FORMAT) => {
  if (!val) {
    return '';
  }
  const [value] = typeof val === 'string' ? val.split('+') : [val];

  // Check if input date is truthy and a valid ISO 8601 date
  if (value && moment(value, moment.ISO_8601).isValid()) {
    // Check if input date is an accepted format of the application (acceptedDateFormats)
    // Use "Strict mode" to validate input format: http://momentjs.com/guides/#/parsing/strict-mode/
    let acceptedFormat = moment(value, acceptedDateInputFormats, true).format(
      dateOutputFormat
    );

    // If the accepted format check fails moment returns 'Invalid date', but we want to return an emptry string
    if (acceptedFormat === 'Invalid date') acceptedFormat = '';

    // If the check passes return the formatted date in 'dateOutputFormat' format
    return acceptedFormat;
  } else {
    // Fails if value is falsey or value is not ISO 8601
    return '';
  }
};

const isEmptyNumber = value => {
  // zero should be still formatted if not null or undefined
  return (!value && value !== 0) || isNaN(value);
};

// format number to comma'd number using Intl.NumberFormat:
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/NumberFormat/format
const formattingOptions = {};
// Note: No support from lower than Android 4.4
const numberFormat = new Intl.NumberFormat('en-US', formattingOptions);
export const toCommaNumber = value => {
  if (isEmptyNumber(value)) {
    return;
  }

  // let NumberFormat format, add back the zeros and dots
  // validation should happen after the format
  let addition = '';
  if (value) {
    const str = value.toString();
    // in case the number is xxx.00 case (NumberFormat strips off decimal including dot)
    if (str.split('.') && Number(str.split('.')[1]) === 0) {
      addition = `.${str.split('.')[1]}`;
      // in case the number is xxx.50 case (NumberFormat strips off the last zero)
    } else if (str.slice(-1) === '0' && str.indexOf('.') >= 0) {
      addition = '0';
    }
  }
  return `${numberFormat.format(value)}${addition}`;
};

export const toCurrency = value => {
  if (isEmptyNumber(value)) {
    return;
  }
  return `$ ${toCommaNumber(value)}`;
};

// used to convert option text (coming from BE) to commaned numbers,
// when there's no translation is specified
export const toOptionText = (localized, value) => {
  if (!value || (localized && localized !== value)) {
    return localized;
  }

  return !isNaN(Number(value)) ? toCommaNumber(Number(value)) : localized;
};

export const toFootInch = value => {
  if (isEmptyNumber(value)) {
    return;
  }

  const feet = Math.floor(value);
  const inches = Math.round((value - feet) * 12);

  return `${feet}' ${inches}"`;
};

// If we need larger date format, bring in library like moment.
export const toUTCDay = value => {
  if (!value) {
    return;
  }

  const date = new Date(value);
  const month = date.getUTCMonth() + 1;
  const day = date.getUTCDate();
  const year = date.getUTCFullYear() - 2000;

  return `${month}/${day}/${year}`;
};

// Assuming strings like primaryUse or PRIMARY_USE or PRIMARY USE
export const toReadableStr = str => {
  if (!str) {
    return;
  }

  return startCase(camelCase(str));
};

// change camelcase string to underscored
export const toUnderscore = str => {
  if (!str) {
    return;
  }

  const arr = [];
  for (let i = 0; i < str.length; i++) {
    const curr = str.charAt(i);
    arr[i] = curr.toUpperCase() === curr ? `_${curr}` : curr;
  }
  return arr.join('').toLowerCase();
};

// queryOptions becomes `Param1 | Value1, Param2 | Value2`
export const queryOptionsToStr = queryOptions => {
  if (!queryOptions) {
    return;
  }

  return queryOptions
    .map(
      opts => `${toReadableStr(opts.key)} | ${toReadableStr(opts.option.key)}`
    )
    .join(', ');
};

// queryOptions becomes { 'Param1 Selected': VALUE1, 'Param2 Selected': VALUE2 }
export const queryOptionsToParams = queryOptions => {
  if (!queryOptions) {
    return;
  }

  return queryOptions.reduce((props, option) => {
    props[`${toReadableStr(option.key)} Selected`] = option.option.key;
    return props;
  }, {});
};

/**
 * used only for manuals so far, because of the unique option format
 * @param {String} key 0
 * @param {String} value xx
 * @param {Array} arr [{ xxId: 0, Name: 'String 0' }, { xxId: 1, Name: 'String 1' }] < want this `Name`
 */
export const getNameFromValue = (key, value, arr) => {
  if (!arr) {
    return value;
  }
  // if nothing matches, just return the value as is
  return arr.reduce(
    (acc, curr) =>
      Number(curr[`${key}Id`]) === Number(value) ? curr.Name : acc,
    value
  );
};

export const MSRPPrice = {
  msrp: {
    title: (
      <FormattedMessage
        id="comparedata.MSRP"
        description="MSRP label"
        defaultMessage="MSRP"
      />
    ),
    format: num => toCurrency(Number(num).toFixed(2))
  }
};

export const dealerPrice = {
  price: {
    title: (
      <FormattedMessage
        id="comparedata.our_price"
        description="Our Price label"
        defaultMessage="Our Price"
      />
    ),
    format: num => toCurrency(Number(num).toFixed(2))
  }
};

/**
 * @function imageSize (used only for ImagesField component so far)
 * @param {Number} size
 */
export const imageSize = size => {
  let formatted = size ? Number(size) / 1024 : 0;
  let unit = 'KB';

  if (formatted > 1024) {
    formatted = formatted / 1024;
    unit = 'MB';
  }
  if (formatted > 1024) {
    formatted = formatted / 1024;
    unit = 'GB';
  }
  // fix to 2 digits decimal
  const split = formatted.toString().split('.');
  if ((split[1] || []).length > 2) {
    formatted = formatted.toFixed(2);
  }
  return `${formatted} ${unit}`;
};

/**
 * @function getFileNameExt (returns array of 0: filename, 1: extension)
 * @param {String} str
 */
export const getFileNameExt = str => {
  if (!str) {
    return;
  }

  const split = str.split('.');
  return [split.slice(0, split.length - 1).join('.'), split[split.length - 1]];
};

/**
 * @function truncateString (used for `ImageField` and `AssetDownloadsTable`)
 * @param {String} str
 * @param {Number} limit
 * @param {Number} trail (last letters before ...) 6 by default
 */
export const truncateString = (str, limit, trail = 6) => {
  if (!str) {
    return;
  }

  return str.length > limit
    ? `${str.slice(0, limit - trail)}...${str.slice(str.length - trail)}`
    : str;
};

/** WHEN THIS UPDATES, PLEASE UPDATE E2E (/tests/e2e/helpers/utils)
 * @function toPhone (used for Warrnty Claim form)
 * @param {String} str
 * [AGIDS-200] under the assumption of North American numbers only
 * and not allowing '+1'
 */
export const toPhone = str => {
  if (!str) {
    return;
  }

  // let's allow longer than 10 digits, this is formatter, not validator
  const regex = /^(\d{3})(\d{3})?(\d{4,})?$/;
  // strip off all non-digital chars
  let cleaned = str.toString().replace(/\D/g, '');
  // if number is longer than 10 digits, remove the first number (likely country code)
  if (cleaned.length > 10) {
    cleaned = cleaned.slice(1);
  }
  return cleaned.replace(regex, `($1) $2-$3`);
};



// WEBPACK FOOTER //
// ./src/utils/formatters.js